import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AssistantLauncher } from '@wianco/portal-assistant';
import { useNavigationStore } from '@/features/navigation/store';
import { useAssistantAccess } from '@/features/assistant/useAssistantAccess';
import { ChevronDown, Cookie, FileLock2, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { NavigationMenu } from '@/features/navigation/components';
import { useNavigation } from '@/features/navigation/hooks';
import { useAuth, useUser } from '@/features/auth/hooks';
import { useConsent } from '@/features/consent/hooks/useConsent';
import { broadcastShellLogout } from '@/features/embedding/utils/logout';
import { useShellI18n } from '@/features/language/useI18n';
import styles from './Sidebar.module.css';

const LOGOUT_PROPAGATION_DELAY_MS = 120;

type SidebarToggleIconProps = {
  collapsed: boolean;
};

function SidebarToggleIcon({ collapsed }: SidebarToggleIconProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 16 16"
      role="presentation"
      aria-hidden="true"
      className={styles.collapseIcon}
    >
      <path
        fill="currentColor"
        fillRule="evenodd"
        d={
          collapsed
            ? 'M14 2.5a.5.5 0 0 1 .5.5v10a.5.5 0 0 1-.5.5h-2v-11zm-3.5 0v11H2a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5zM16 3a2 2 0 0 0-2-2H2a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2zM8.03 7.47l-2.5-2.5-1.06 1.06L6.44 8 4.47 9.97l1.06 1.06 2.5-2.5a.75.75 0 0 0 0-1.06'
            : 'M2 2.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h2v-11zm3.5 0v11H14a.5.5 0 0 0 .5-.5V3a.5.5 0 0 0-.5-.5zM0 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm7.97 4.47 2.5-2.5 1.06 1.06L9.56 8l1.97 1.97-1.06 1.06-2.5-2.5a.75.75 0 0 1 0-1.06'
        }
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Sidebar navigation component.
 */
export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, setMobileMenuOpen } = useNavigation();
  const assistantOpen = useNavigationStore((state) => state.assistantOpen);
  const { canViewAssistant } = useAssistantAccess();
  const toggleAssistant = useNavigationStore((state) => state.toggleAssistant);
  const { logout } = useAuth();
  const { user } = useUser();
  const { openSettings } = useConsent();
  const { t } = useShellI18n();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userInitials = user?.email ? user.email.slice(0, 2).toUpperCase() : 'U';

  useEffect(() => {
    if (!userMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current) {
        return;
      }
      if (event.target instanceof Node && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    if (!sidebarCollapsed) {
      setUserMenuOpen(false);
    }
  }, [sidebarCollapsed]);

  const closeMenus = () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const handleOpenSettings = () => {
    closeMenus();
    openSettings();
  };

  const handleLogout = () => {
    closeMenus();
    broadcastShellLogout();
    window.setTimeout(() => {
      void logout();
    }, LOGOUT_PROPAGATION_DELAY_MS);
  };

  return (
    <aside
      className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}
      aria-label={t('Main navigation')}
    >
      <div className={styles.header}>
        {!sidebarCollapsed && (
          <div className={styles.brand}>
            {/*
              Die orange Variante, weil das Zeichen seit dem Wegfall der Kachel direkt
              auf der Leistenflaeche steht. Das weisse Symbol waere im hellen Theme
              unsichtbar.
            */}
            <img
              className={styles.brandLogo}
              src="/logos/wianco-symbol-orange.svg"
              alt=""
              aria-hidden="true"
            />
            <span className={styles.logoText}>WIANCO Portal</span>
          </div>
        )}

        <button
          onClick={toggleSidebar}
          className={styles.collapseButton}
          aria-label={sidebarCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
          type="button"
        >
          <SidebarToggleIcon collapsed={sidebarCollapsed} />
        </button>
      </div>

      <div className={styles.content} onClick={() => setMobileMenuOpen(false)}>
        <NavigationMenu />
      </div>

      <div
        className={styles.footer}
        data-assistant-open={(canViewAssistant && assistantOpen) || undefined}
      >
        {canViewAssistant && (
          <AssistantLauncher
            showLabel={!sidebarCollapsed}
            open={assistantOpen}
            onToggle={toggleAssistant}
          />
        )}
        <div className={styles.userMenuContainer} ref={userMenuRef}>
          <button
            type="button"
            className={styles.userProfileButton}
            onClick={() => setUserMenuOpen((current) => !current)}
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            aria-label={t('Open user menu')}
          >
            <span className={styles.avatar}>{userInitials}</span>
            {!sidebarCollapsed && (
              <>
                <span className={styles.userInfo}>
                  <span className={styles.userName}>{user?.email ?? t('Not signed in')}</span>
                  <span className={styles.userRole}>{user ? t('Authenticated') : t('Guest')}</span>
                </span>
                <span
                  className={`${styles.userChevron} ${userMenuOpen ? styles.userChevronOpen : ''}`}
                >
                  <ChevronDown aria-hidden="true" />
                </span>
              </>
            )}
          </button>

          {userMenuOpen && (
            <div
              className={`${styles.userMenu} ${sidebarCollapsed ? styles.userMenuCollapsed : ''}`}
              role="menu"
              aria-label={t('User actions')}
            >
              <Link
                to="/settings"
                className={styles.userMenuItem}
                onClick={closeMenus}
                role="menuitem"
              >
                <Settings className={styles.menuIcon} aria-hidden="true" />
                <span>{t('Settings')}</span>
              </Link>
              <button
                type="button"
                className={styles.userMenuItem}
                onClick={handleOpenSettings}
                role="menuitem"
              >
                <Cookie className={styles.menuIcon} aria-hidden="true" />
                <span>{t('Cookies')}</span>
              </button>
              <Link
                to="/privacy-policy"
                className={styles.userMenuItem}
                onClick={closeMenus}
                role="menuitem"
              >
                <ShieldCheck className={styles.menuIcon} aria-hidden="true" />
                <span>{t('Data Privacy')}</span>
              </Link>
              <Link
                to="/data-protection"
                className={styles.userMenuItem}
                onClick={closeMenus}
                role="menuitem"
              >
                <FileLock2 className={styles.menuIcon} aria-hidden="true" />
                <span>{t('Data Protection')}</span>
              </Link>
              <button
                type="button"
                className={styles.userMenuItem}
                onClick={handleLogout}
                role="menuitem"
              >
                <LogOut className={styles.menuIcon} aria-hidden="true" />
                <span>{t('Logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
